export default function JSVEditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - JavaScript &amp; React Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-08-02">August 2, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built the{" "}
            <strong>JavaScript &amp; React Editor</strong> (JSVEdit) app as
            an ad-supported application with an optional Pro upgrade. This
            Privacy Policy explains what information JSVEdit processes, how
            that information is used, and the choices you have when using
            the app.
          </p>

          <div className="rounded-lg border border-[#E1DCCE] bg-[#FFFDF7] p-5">
            <h2 className="text-xl font-bold text-[#A8720E]">
              The short version
            </h2>
            <p className="mt-2">
              Your code stays on your device by default. Two things send data
              off your device, and both happen only because you asked for
              them: importing an <strong>npm package</strong> fetches that
              package from a public CDN, and using{" "}
              <strong>Publish to the web</strong> uploads that project so it
              can be served at a public link. Everything else — editing,
              running, previewing, console output — runs locally.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>JavaScript, TypeScript and JSX source files:</strong>{" "}
              code you write, open, or import is stored on your device and
              executed on your device. It is not uploaded to us unless you
              use the Publish feature described below.
            </li>
            <li>
              <strong>React and TypeScript preview rendering:</strong> the
              React runtime and the JSX/TypeScript transpiler are bundled
              inside the app, so previews of self-contained projects render
              without network access.
            </li>
            <li>
              <strong>Console output:</strong> logs, warnings, and errors
              from your program are displayed locally and are not
              transmitted to us.
            </li>
            <li>
              <strong>Published projects:</strong> if you choose to publish,
              the project&apos;s code is uploaded to our hosting backend and
              served publicly. See{" "}
              <a href="#publishing" className="text-[#0F6BBF] underline">
                Publishing to the Web
              </a>
              .
            </li>
            <li>
              <strong>An anonymous account identifier:</strong> publishing
              creates an anonymous Firebase Authentication identifier so the
              app can tell which published pages are yours and let you
              update or remove them. It contains no name, email address,
              phone number, or other personal identifier, and you are never
              asked to sign in or create an account.
            </li>
            <li>
              <strong>npm package names:</strong> when your code imports an
              npm package, the package name and version are requested from
              the public CDN that serves it. Your own source code is not
              sent in that request.
            </li>
            <li>
              <strong>App preferences:</strong> your theme choice, editor
              settings, snippets, and session history are stored locally on
              your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob
              or related Google Mobile Ads services may collect device and
              advertising data as described in Google&apos;s own privacy
              documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics
              and Firebase Crashlytics may collect app stability, usage
              patterns, and crash reports to improve reliability and user
              experience.
            </li>
          </ul>

          <h2
            id="publishing"
            className="scroll-mt-6 text-2xl font-bold text-[#A8720E]"
          >
            Publishing to the Web
          </h2>
          <p>
            Publish to the web is an optional Pro feature. It is never
            automatic — a project leaves your device only when you tap
            Publish for that project.
          </p>
          <ul className="mt-4 list-disc space-y-2 pl-6">
            <li>
              <strong>What is uploaded:</strong> the code of the project you
              publish, bundled into a single web page, together with the
              title you give it. Multi-file projects are uploaded in full.
              Other projects, saved files, preferences, and console history
              are not uploaded.
            </li>
            <li>
              <strong>Where it is stored:</strong> Google Firebase Realtime
              Database, and it is served from our hosting at{" "}
              <code className="rounded bg-[#F2EEE2] px-1">
                js-vedit.web.app
              </code>
              .
            </li>
            <li>
              <strong>Who can see it:</strong>{" "}
              <strong>anyone with the link.</strong> Published pages are not
              password protected. The address contains a random identifier
              and we do not list published pages anywhere, but the page is
              public, and anyone you share the link with can open it and can
              pass it on. Please do not publish code that contains
              passwords, API keys, access tokens, or personal information.
            </li>
            <li>
              <strong>Updating and removing:</strong> publishing the same
              project again replaces the contents at the same link. You can
              unpublish any page from &quot;My published pages&quot; inside
              the app, which deletes it from our database and takes the link
              offline. Copies that other people already saved or shared are
              outside our control.
            </li>
            <li>
              <strong>How long it is kept:</strong> a published page stays
              online until you unpublish it, or until we remove it under the
              Reporting and Takedown section below.
            </li>
            <li>
              <strong>Viewing a published page:</strong> published pages load
              the JSX transpiler, and any npm packages the project imports,
              from public CDNs. Those CDNs receive the ordinary request
              information — such as an IP address and browser user agent —
              of whoever opens the page.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To let you write and run JavaScript and TypeScript directly on
              your device.
            </li>
            <li>To render your React components as a live preview.</li>
            <li>
              To display console output, errors, and stack traces inline.
            </li>
            <li>
              To resolve the npm packages your code imports so they run.
            </li>
            <li>
              To store and serve the projects you explicitly choose to
              publish, and to associate them with your anonymous identifier
              so only you can change or remove them.
            </li>
            <li>
              To remember your editor settings, session history, and
              preferences.
            </li>
            <li>
              To improve app performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the app, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage</h2>
          <p>
            Apart from the projects you publish, JSVEdit keeps your data on
            your device. Source files, sessions, snippets, preferences, and
            preview caches are stored locally. That data remains on your
            device until you remove it, clear the app&apos;s storage, or
            uninstall the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Network Access
          </h2>
          <p>
            Writing and running a self-contained project does not require
            network access — the React runtime and the JSX/TypeScript
            transpiler are bundled with the app. The app makes network
            requests in these cases:
          </p>
          <ul className="mt-4 list-disc space-y-2 pl-6">
            <li>
              Resolving npm packages your code imports, from{" "}
              <code className="rounded bg-[#F2EEE2] px-1">esm.sh</code>
            </li>
            <li>
              Loading the code formatter and the JSX transpiler used for
              published pages, from{" "}
              <code className="rounded bg-[#F2EEE2] px-1">unpkg.com</code>{" "}
              and{" "}
              <code className="rounded bg-[#F2EEE2] px-1">
                cdn.jsdelivr.net
              </code>
            </li>
            <li>Uploading and removing pages you publish</li>
            <li>Ads, analytics, and Crashlytics diagnostics</li>
            <li>
              The rotating cross-promotion banner shown on the home screen
            </li>
            <li>Purchases and entitlement checks through Google Play</li>
          </ul>
          <p className="mt-4">
            Other than projects you publish, your source code, file
            contents, and console output are not sent over the network.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            JSVEdit may use <strong>Google AdMob</strong> to show
            interstitial and app-open ad formats. AdMob may collect and
            process identifiers, device information, approximate location,
            and ad interaction data to deliver and measure ads. Upgrading to
            Pro removes ads.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            JSVEdit offers an optional Pro upgrade, available as a
            renewing subscription or a one-time purchase. Purchases and
            renewals are processed by Google Play; we do not receive or
            store your payment details. Purchases are subject to
            Google&apos;s terms and privacy policies, and subscriptions are
            managed from your Google Play account.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>JSVEdit relies on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services and Google Play Billing</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
            <li>
              Firebase Authentication, Realtime Database, and Hosting — used
              only for the Publish feature
            </li>
            <li>
              The public package and tooling CDNs{" "}
              <code className="rounded bg-[#F2EEE2] px-1">esm.sh</code>,{" "}
              <code className="rounded bg-[#F2EEE2] px-1">unpkg.com</code>,
              and{" "}
              <code className="rounded bg-[#F2EEE2] px-1">
                cdn.jsdelivr.net
              </code>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your source code, project files, or personal
            data. Data may be shared only with service providers required to
            operate app features, such as hosting, advertising, analytics,
            app distribution, or diagnostics infrastructure. Projects you
            publish are made publicly accessible by design, at your request.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it, clear
            the app&apos;s storage, or uninstall the app. Published pages
            remain on our servers until you unpublish them or we remove them
            under the Reporting and Takedown section. Data collected by
            third-party services such as AdMob and Firebase is retained
            according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Reporting and Takedown
          </h2>
          <p>
            Every published page carries a report link. If a published page
            contains unlawful, abusive, deceptive, or infringing content, or
            content published without the rights holder&apos;s permission,
            write to{" "}
            <a
              href="mailto:support@cosmovex.in"
              className="text-[#0F6BBF] underline"
            >
              support@cosmovex.in
            </a>{" "}
            with the link. We remove pages that break these rules, and we
            may remove a published page or withdraw access to the Publish
            feature where it is being misused.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            JSVEdit is not intended for children under 13, and we do not
            knowingly collect personal information from children through the
            app. The Publish feature makes content publicly accessible on
            the internet and is not intended for use by children.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            Published pages can only be changed or removed by the anonymous
            identifier that created them, enforced by our database security
            rules. We take reasonable steps to protect data processed by the
            app, but no method of storage or transmission is completely
            secure, and a published page is public to anyone holding its
            link.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>You can delete individual files from inside the app.</li>
            <li>
              You can choose never to publish; nothing is published unless
              you tap Publish.
            </li>
            <li>
              You can unpublish any page at any time from &quot;My published
              pages&quot;, which deletes it from our servers.
            </li>
            <li>
              You can uninstall the app to remove all locally stored data
              from your device. Uninstalling does not remove pages you have
              already published — unpublish them first, or write to us to
              request removal.
            </li>
            <li>You can upgrade to Pro to disable all advertising.</li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will post the updated version on this page and revise the
            effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about JavaScript &amp; React
            Editor, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . For reports about a published page, contact{" "}
            <a
              href="mailto:support@cosmovex.in"
              className="text-[#0F6BBF] underline"
            >
              support@cosmovex.in
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
